import { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, Phone, Lock, Heart, Stethoscope, Truck, Calendar, ArrowLeft } from "lucide-react";
import { authService } from "../../services";
import { useApi } from "../../hooks";
import { AuthContext } from "../../context/AuthContext";

export default function Login() {
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const { loading, error: apiError, execute } = useApi(authService.login);

  const validateForm = () => {
    const newErrors = {};
    
    if (!mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^\d{10}$/.test(mobile.replace(/\D/g, ""))) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number";
    }
    
    if (!password.trim()) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setErrors({});

    try {
      // Remove spaces from mobile number before sending to API
      const cleanMobile = mobile.replace(/\s/g, '');

      const response = await execute(cleanMobile, password);

      // authService automatically stores tokens in localStorage
      // Response structure: { success, tokens: { access, refresh }, user: { id, username, mobile, ... } }
      // Update AuthContext with user data
      login({
        access: authService.getAccessToken(),
        refresh: localStorage.getItem('refresh_token'),
        user: response.user || { mobile: cleanMobile, username: response.user?.username }
      });

      navigate("/");
    } catch (error) {
      console.error("Login error:", error);
      setErrors({ general: error.message || "Invalid mobile number or password. Please try again." });
    }
  };

  const formatMobileNumber = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{5})(\d{5})/, "$1 $2");
    }
    return numbers.slice(0, 10).replace(/(\d{5})(\d{5})/, "$1 $2");
  };

  const handleMobileChange = (e) => {
    const formatted = formatMobileNumber(e.target.value);
    setMobile(formatted);
    if (errors.mobile) {
      setErrors(prev => ({ ...prev, mobile: "" }));
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (errors.password) {
      setErrors(prev => ({ ...prev, password: "" }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex">
      {/* Left Side - Information Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-green-600 p-12 flex-col justify-center relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-32 h-32 rounded-full bg-white"></div>
          <div className="absolute bottom-20 right-20 w-24 h-24 rounded-full bg-white"></div>
          <div className="absolute top-1/2 left-1/3 w-16 h-16 rounded-full bg-white"></div>
        </div>
        
        <div className="relative z-10 text-white">
          <Link to="/" className="flex items-center mb-8 hover:opacity-90 transition-opacity">
            <Heart className="w-10 h-10 mr-3 text-red-300" />
            <h1 className="text-4xl font-bold">Home Cure Express</h1>
          </Link>
          
          <h2 className="text-2xl font-semibold mb-6 leading-tight">
            Trusted Healthcare at Your Doorstep
          </h2>
          
          <p className="text-lg mb-12 text-blue-100 leading-relaxed">
            Experience convenient, reliable healthcare services from the comfort of your home. 
            We're here to make quality medical care accessible to everyone.
          </p>
          
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <Stethoscope className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Online Medicine Ordering</h3>
                <p className="text-blue-100">Get your prescriptions delivered safely to your door</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <Heart className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Home Healthcare Services</h3>
                <p className="text-blue-100">Professional paramedical care in your home</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <Truck className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Emergency Support</h3>
                <p className="text-blue-100">24/7 ambulance and emergency services</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <Calendar className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Health Monitoring</h3>
                <p className="text-blue-100">Regular check-ups and health follow-ups</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Header (visible on small screens) */}
          <div className="lg:hidden mb-8">
            <button onClick={() => navigate('/')} className="flex items-center text-gray-600 hover:text-gray-800 mb-4 transition-colors">
              <ArrowLeft className="w-5 h-5 mr-1" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <div className="text-center">
              <Link to="/" className="inline-flex items-center justify-center mb-2 hover:opacity-80 transition-opacity">
                <Heart className="w-8 h-8 mr-2 text-blue-600" />
                <h1 className="text-2xl font-bold text-gray-800">Home Cure Express</h1>
              </Link>
              <p className="text-gray-600">Healthcare at your doorstep</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="hidden lg:flex items-center justify-between mb-6">
              <button onClick={() => navigate('/')} className="flex items-center text-gray-500 hover:text-gray-700 transition-colors">
                <ArrowLeft className="w-4 h-4 mr-1" />
                <span className="text-sm font-medium">Back</span>
              </button>
            </div>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome Back</h2>
              <p className="text-gray-600">Sign in to access your healthcare dashboard</p>
            </div>

            {errors.general && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{errors.general}</p>
              </div>
            )}

            <div className="space-y-6">
              {/* Mobile Number Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    placeholder="Enter mobile number"
                    value={mobile}
                    onChange={handleMobileChange}
                    className={`auth-input w-full pl-14 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                      errors.mobile
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    maxLength="11"
                  />
                </div>
                {errors.mobile && (
                  <p className="mt-2 text-sm text-red-600">{errors.mobile}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={handlePasswordChange}
                    className={`auth-input w-full pl-14 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                      errors.password
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowPassword(!showPassword);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors flex items-center justify-center w-8 h-8 rounded"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600">{errors.password}</p>
                )}
              </div>

              {/* Login Button */}
              <button
                onClick={submit}
                disabled={loading}
                className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Signing in...</span>
                  </div>
                ) : (
                  'Sign In'
                )}
              </button>

              {/* Links */}
              <div className="space-y-4 text-center">
                <Link 
                  to="/ForgotPassword" 
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                >
                  Forgot your password?
                </Link>
                
                <div className="text-gray-600 text-sm">
                  New to Home Cure Express?{' '}
                  <Link 
                    to="/signup" 
                    className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
                  >
                    Create an account
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}